// Line-based diff for the AI-fix preview (doc-87). Plain LCS over lines —
// schema documents are small (a few hundred lines), so O(n·m) is fine; a
// guard falls back to whole-document replace far above anything realistic.

export function lineDiff(beforeText, afterText) {
  const a = String(beforeText ?? '').split('\n');
  const b = String(afterText ?? '').split('\n');
  const n = a.length;
  const m = b.length;
  if (n * m > 4_000_000) {
    return [
      ...a.map((line) => ({ op: 'del', line })),
      ...b.map((line) => ({ op: 'add', line })),
    ];
  }
  const lcs = Array.from({ length: n + 1 }, () => new Uint32Array(m + 1));
  for (let i = n - 1; i >= 0; i -= 1) {
    for (let j = m - 1; j >= 0; j -= 1) {
      lcs[i][j] = a[i] === b[j]
        ? lcs[i + 1][j + 1] + 1
        : Math.max(lcs[i + 1][j], lcs[i][j + 1]);
    }
  }
  const out = [];
  let i = 0;
  let j = 0;
  while (i < n && j < m) {
    if (a[i] === b[j]) {
      out.push({ op: 'ctx', line: a[i] });
      i += 1; j += 1;
    } else if (lcs[i + 1][j] >= lcs[i][j + 1]) {
      out.push({ op: 'del', line: a[i] });
      i += 1;
    } else {
      out.push({ op: 'add', line: b[j] });
      j += 1;
    }
  }
  while (i < n) { out.push({ op: 'del', line: a[i] }); i += 1; }
  while (j < m) { out.push({ op: 'add', line: b[j] }); j += 1; }
  return out;
}

// Collapse long unchanged runs to `context` lines on each side of a change,
// replacing the middle with { op: 'skip', count } rows — keeps a one-line fix
// in a long document scannable.
export function collapseContext(ops, context = 2) {
  const keep = new Array(ops.length).fill(false);
  ops.forEach((o, idx) => {
    if (o.op === 'ctx') return;
    for (let k = Math.max(0, idx - context); k <= Math.min(ops.length - 1, idx + context); k += 1) {
      keep[k] = true;
    }
  });
  const out = [];
  let skipped = 0;
  const flush = () => {
    if (skipped > 0) out.push({ op: 'skip', count: skipped });
    skipped = 0;
  };
  ops.forEach((o, idx) => {
    if (o.op !== 'ctx' || keep[idx]) {
      flush();
      out.push(o);
    } else {
      skipped += 1;
    }
  });
  flush();
  return out;
}
